/** What saving `tour.join` did (`ConsoleCatalogueJoinOutcome`; plan 2026-10-11 product joins §9):
 *  the IDs written onto the joined product's tour, the ones that were there already, the ones moved
 *  off other tours (an old tour's own ID renamed `OFF/<id>`, an additional ID retargeted), a join
 *  deferred until that tour exists, and the Edit-booking tasks started to move the bookings (D5),
 *  each linked to its task. A join that put back IDs it held before (`recreated`: deleted since,
 *  most likely by hand) says so, and how to detach the product instead. The settings drawer shows
 *  one after a save; the bulk dialog one per product. */
import { Box, Link, Typography } from '@mui/material';
import { Fragment, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { JoinedId, JoinOutcome } from '@/api/catalogue';

import { plural, tourLabel } from '../JoinOwnership';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const Code = ({ children }: { children: ReactNode }) => (
  <Box component="code" sx={{ fontFamily: MONO }}>
    {children}
  </Box>
);

function ids(list: readonly Pick<JoinedId, 'custom_id'>[]): ReactNode {
  return list.map((x, i) => (
    <Fragment key={x.custom_id}>
      {i > 0 && ', '}
      <Code>{x.custom_id}</Code>
    </Fragment>
  ));
}

/** A background task, linked to its details. */
export function TaskLink({ id }: { id: string }) {
  return (
    <Link component={RouterLink} to={`/bgtasks/${id}`}>
      <Code>{id}</Code>
    </Link>
  );
}

/** One product's join outcome as lines. `name` is the joining product's. */
export function JoinOutcomeLines({ outcome, name, intoName }: { outcome: JoinOutcome; name: string; intoName?: string }) {
  const into = intoName || outcome.into_vendor_id;
  const tour = outcome.tour_id !== null ? `${tourLabel({ id: outcome.tour_id, name: outcome.tour_name })} (#${outcome.tour_id})` : null;
  const moves = outcome.booking_moves;
  const covered = new Set((moves?.launched ?? []).map((m) => m.task_id));
  const unlisted = outcome.booking_move_task_ids.filter((id) => !covered.has(id));
  const lines: ReactNode[] = [];

  if (outcome.deferred) {
    lines.push(
      <>
        {name} joins {into}, whose tour does not exist yet: nothing was written. Its IDs join that tour when
        its sync creates it{outcome.consent_stored ? ', with the answers given here' : ''}.
      </>,
    );
  } else {
    lines.push(
      <>
        {name} joins the tour of {into}
        {tour ? `, ${tour}` : ''}: it builds no tour of its own.
      </>,
    );
    if (outcome.created.length) {
      lines.push(
        <>
          {outcome.created.length === 1 ? 'New additional ID' : `${outcome.created.length} new additional IDs`}:{' '}
          {ids(outcome.created)}.
        </>,
      );
    }
    if (outcome.recreated) {
      lines.push(
        <>
          Re-created IDs the join held before — to detach {name}, clear its join instead of deleting
          its IDs.
        </>,
      );
    }
    if (outcome.already.length) {
      lines.push(<>Already additional IDs of that tour: {ids(outcome.already)}.</>);
    }
    for (const m of outcome.moved) {
      const from = tourLabel({ id: m.from_tour_id, name: m.from_tour_name });
      lines.push(
        m.kind === 'primary' ? (
          <>
            <Code>{m.custom_id}</Code> moved from {from}, whose own ID is now{' '}
            {m.parked_as ? <Code>{m.parked_as}</Code> : 'parked'}.
          </>
        ) : (
          <>
            <Code>{m.custom_id}</Code> moved from {from}
            {m.taken_over ? '; the import no longer maintains it' : ''}.
          </>
        ),
      );
    }
  }
  for (const m of moves?.launched ?? []) {
    lines.push(
      <>
        Moving {plural(m.count, 'booking', 'bookings')} off {tourLabel({ id: m.from_tour_id, name: m.from_tour_name })}:{' '}
        task <TaskLink id={m.task_id} />.
      </>,
    );
  }
  for (const id of unlisted) {
    lines.push(
      <>
        Booking move started: task <TaskLink id={id} />.
      </>,
    );
  }
  for (const m of moves?.skipped ?? []) lines.push(m.detail);
  for (const m of moves?.errors ?? []) {
    lines.push(
      <Typography component="span" variant="body2" color="error">
        Bookings not moved: {m.detail}
      </Typography>,
    );
  }

  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {lines.map((l, i) => (
        <Typography key={i} component="li" variant="body2">
          {l}
        </Typography>
      ))}
    </Box>
  );
}
